/* DGS cart drawer — vanilla custom element, no dependencies.
 * All cart HTML comes from the server via the Section Rendering API;
 * this file only swaps HTML and manages open/close/mutation plumbing.
 * GTM note: the add_to_cart trigger is a CLICK binding on the product form's
 * submit button (dataLayer-allPages.liquid). We intercept SUBMIT, not click,
 * so existing analytics fire exactly as before. Do not rename the product
 * form markup from here.
 */
(function () {
  'use strict';

  var SECTION = 'cart-drawer';

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  var CartDrawer = function () {};

  function initDrawer(root) {
    var panel = root.querySelector('.cd__panel');
    var routes = {
      add: root.getAttribute('data-cart-add-url') || '/cart/add',
      change: root.getAttribute('data-cart-change-url') || '/cart/change',
      update: root.getAttribute('data-cart-update-url') || '/cart/update'
    };

    var state = {
      open: false,
      queue: Promise.resolve(),
      qtyTimers: {},
      lineErrors: {},
      cartItems: [],
      lastTrigger: null,
      scrollY: 0,
      addInFlight: false
    };

    /* ---------- open / close ---------- */

    function lockScroll() {
      state.scrollY = window.scrollY || window.pageYOffset;
      document.body.style.position = 'fixed';
      document.body.style.top = -state.scrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }

    function unlockScroll() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, state.scrollY);
    }

    function open(trigger) {
      if (state.open) return;
      state.open = true;
      state.lastTrigger = trigger || document.activeElement;
      root.classList.add('cd--open');
      document.documentElement.classList.add('smile-ui-hidden', 'cd-drawer-open');
      // re-sync from the server: apps (Appstle etc.) can mutate the cart
      // without going through this element, leaving the drawer stale
      refresh();
      lockScroll();
      var closeBtn = root.querySelector('.cd__close');
      (closeBtn || panel).focus({ preventScroll: true });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'cart_open' });
      var addonIds = Array.prototype.map.call(root.querySelectorAll('[data-cd-addon]'), function (b) {
        return b.getAttribute('data-cd-addon');
      });
      if (addonIds.length) {
        window.dataLayer.push({ event: 'addon_view', addon_variant_ids: addonIds });
      }
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      if (!state.open) return;
      state.open = false;
      root.classList.remove('cd--open');
      document.documentElement.classList.remove('smile-ui-hidden', 'cd-drawer-open');
      unlockScroll();
      document.removeEventListener('keydown', onKeydown);
      if (state.lastTrigger && typeof state.lastTrigger.focus === 'function') {
        state.lastTrigger.focus({ preventScroll: true });
      }
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      var focusables = panel.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, summary, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* ---------- rendering ---------- */

    function contentEl() {
      return root.querySelector('[data-cd-content]');
    }

    function footerEl() {
      return root.querySelector('.cd__footer');
    }

    function renderSection(html) {
      var current = contentEl();
      if (!current) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var next = doc.querySelector('[data-cd-content]');
      if (!next) return;
      Object.keys(state.lineErrors).forEach(function (key) {
        var line = findLine(key, next);
        if (line) showLineError(line, state.lineErrors[key].message);
      });
      var currentFooter = footerEl();
      var nextFooter = doc.querySelector('.cd__footer');
      var contentChanged = next.innerHTML !== current.innerHTML;
      var footerChanged = currentFooter && nextFooter && nextFooter.innerHTML !== currentFooter.innerHTML;
      // no-op refreshes (e.g. the re-sync on open) must not shift UI
      // under the user's finger
      if (!contentChanged && !footerChanged) return;
      if (contentChanged) {
        var contentScrollTop = current.scrollTop;
        var items = current.querySelector('[data-cd-items]');
        var scrollTop = items ? items.scrollTop : 0;
        var noteFocused = document.activeElement === current.querySelector('[data-cd-note]');
        current.replaceWith(next);
        next.scrollTop = contentScrollTop;
        var newItems = next.querySelector('[data-cd-items]');
        if (newItems) newItems.scrollTop = scrollTop;
        if (noteFocused) {
          var note = next.querySelector('[data-cd-note]');
          if (note) {
            var d = next.querySelector('.cd__notes');
            if (d) d.open = true;
            note.focus({ preventScroll: true });
          }
        }
      }
      if (footerChanged) currentFooter.replaceWith(nextFooter);
      var count = next.getAttribute('data-item-count');
      if (count !== null) {
        updateHeaderCount(count);
        root.setAttribute('data-empty', count === '0' ? 'true' : 'false');
        var live = root.querySelector('[data-cd-live]');
        if (live) {
          var checkoutBtn = next.querySelector('.cd__checkout');
          var totalText = checkoutBtn ? (checkoutBtn.textContent.split('•')[1] || '').trim() : '';
          live.textContent = 'Cart updated: ' + count + (count === '1' ? ' item' : ' items') + (totalText ? ', total ' + totalText : '');
        }
      }
    }

    function updateHeaderCount(count) {
      document.querySelectorAll('.cart-button span, .cd__bubble-count').forEach(function (el) {
        el.textContent = count;
      });
    }

    /* ---------- mutations (serialized: cart endpoints are not concurrency-safe) ---------- */

    function enqueue(fn) {
      state.queue = state.queue.then(fn, fn);
      return state.queue;
    }

    function mutate(url, body) {
      return enqueue(function () {
        body.sections = SECTION;
        body.sections_url = window.location.pathname;
        return fetch(url + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(body)
        }).then(function (res) {
          return res.json().then(function (json) {
            if (!res.ok) {
              var err = new Error(json.description || json.message || 'Cart error');
              err.status = res.status;
              throw err;
            }
            if (url !== routes.add && Array.isArray(json.items)) {
              syncLineErrors(json.items);
            }
            if (json.sections && json.sections[SECTION]) {
              renderSection(json.sections[SECTION]);
            }
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: json }));
            return json;
          });
        });
      });
    }

    function changeLine(key, quantity, lineEl) {
      var previousError = state.lineErrors[key];
      var identity = null;
      state.cartItems.forEach(function (item) {
        if (item.key === key) identity = lineIdentity(item);
      });
      return mutate(routes.change, { id: key, quantity: quantity }).then(function (cart) {
        Object.keys(state.lineErrors).forEach(function (errorKey) {
          if (errorKey !== key && state.lineErrors[errorKey] !== previousError) return;
          delete state.lineErrors[errorKey];
          var line = findLine(errorKey, root);
          var error = line && line.querySelector('[data-cd-line-error]');
          if (error) {
            error.textContent = '';
            error.hidden = true;
          }
        });
        return cart;
      }).catch(function (err) {
        state.lineErrors[key] = { message: err.message, identity: identity };
        showLineError(findLine(key, root) || lineEl, err.message);
        return refresh();
      });
    }

    function refresh() {
      // no-op update: cheapest way to re-render from real server state
      return mutate(routes.update, {}).catch(function () {});
    }

    // Remove the no-design line of a product once a designed line exists —
    // uses the CURRENT render's key (line keys are not stable across renders).
    function removeNoDesignOrphan(productId) {
      var orphan = null;
      root.querySelectorAll('.cd-item[data-product-id="' + productId + '"]').forEach(function (li) {
        if (!orphan && li.querySelector('.cd-item__nodesign') && !li.querySelector('[data-cd-reorder]')) orphan = li;
      });
      // the reorder button lives on the orphan itself; match it too
      if (!orphan) {
        root.querySelectorAll('.cd-item[data-product-id="' + productId + '"]').forEach(function (li) {
          if (!orphan && (li.querySelector('.cd-item__nodesign') || li.querySelector('[data-cd-reorder]'))) orphan = li;
        });
      }
      if (!orphan) return;
      changeLine(orphan.getAttribute('data-key'), 0, orphan);
    }

    // "No Design Added — tap to add yours" links back to the product page
    // with ?dgs_fix_design=1. When the customer then adds a DESIGNED line of
    // that product, drop the old no-design orphan — otherwise they'd buy both.
    function maybeRemoveOrphan(addedItem) {
      if (window.location.search.indexOf('dgs_fix_design=1') === -1) return;
      if (!addedItem || !addedItem.product_id) return;
      var props = addedItem.properties || {};
      if (!props['_DGS Design URL'] && !props['Disc Design Url']) return; // added without a design — keep the orphan
      removeNoDesignOrphan(addedItem.product_id);
      // one replacement per visit — a second add is a deliberate extra disc
      var url = new URL(window.location.href);
      url.searchParams.delete('dgs_fix_design');
      window.history.replaceState({}, '', url.toString());
    }

    function lineIdentity(item) {
      var properties = item.properties || {};
      var plan = item.selling_plan_allocation;
      return JSON.stringify([
        item.variant_id,
        plan && plan.selling_plan ? plan.selling_plan.id : null,
        Object.keys(properties).sort().map(function (name) {
          return [name, properties[name]];
        })
      ]);
    }

    function syncLineErrors(items) {
      var errors = {};
      Object.keys(state.lineErrors).forEach(function (key) {
        var error = state.lineErrors[key];
        var matches = items.filter(function (item) {
          return error.identity ? lineIdentity(item) === error.identity : item.key === key;
        });
        if (matches.length === 1) errors[matches[0].key] = error;
      });
      state.lineErrors = errors;
      state.cartItems = items;
    }

    function findLine(key, container) {
      var lines = container.querySelectorAll('.cd-item[data-key]');
      for (var i = 0; i < lines.length; i++) {
        if (lines[i].getAttribute('data-key') === key) return lines[i];
      }
      return null;
    }

    function showLineError(lineEl, message) {
      if (!lineEl) return;
      var el = lineEl.querySelector('[data-cd-line-error]');
      if (!el) return;
      el.textContent = message;
      el.hidden = false;
    }

    /* ---------- event delegation inside the drawer ---------- */

    root.addEventListener('click', function (e) {
      var t = e.target;

      if (t.closest('[data-cd-close]')) {
        e.preventDefault();
        close();
        return;
      }

      var qtyBtn = t.closest('[data-cd-qty]');
      if (qtyBtn) {
        var line = qtyBtn.closest('.cd-item');
        var input = line.querySelector('[data-cd-qty-input]');
        var next = (parseInt(input.value, 10) || 0) + parseInt(qtyBtn.getAttribute('data-cd-qty'), 10);
        // minus stops at 1 — removal is the trash icon's job, accidental
        // deletion of a designed disc is not recoverable
        if (next < 1) return;
        input.value = next; // optimistic
        var minusBtn = line.querySelector('[data-cd-qty="-1"]');
        if (minusBtn) minusBtn.disabled = next <= 1;
        scheduleQty(line, next);
        return;
      }

      var removeBtn = t.closest('[data-cd-remove]');
      if (removeBtn) {
        var rline = removeBtn.closest('.cd-item');
        rline.style.opacity = '0.4';
        changeLine(rline.getAttribute('data-key'), 0, rline);
        return;
      }

      var addonBtn = t.closest('[data-cd-addon]');
      if (addonBtn) {
        addonBtn.disabled = true;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'addon_add', addon_variant_id: addonBtn.getAttribute('data-cd-addon') });
        mutate(routes.add, { id: parseInt(addonBtn.getAttribute('data-cd-addon'), 10), quantity: 1 }).catch(function (err) {
          addonBtn.disabled = false;
          addonBtn.textContent = 'Unavailable';
        });
        return;
      }

      var miniBtn = t.closest('[data-cd-mini]');
      if (miniBtn) {
        var mline = miniBtn.closest('.cd-item');
        miniBtn.disabled = true;
        miniBtn.classList.add('cd-item__miniupsell--busy');
        var props = {
          '_DGS Product ID': miniBtn.getAttribute('data-cd-mini-product'),
          '_DGS Variant ID': miniBtn.getAttribute('data-cd-mini'),
          '_DGS Design URL': mline.getAttribute('data-design-url') || '',
          '_DGS Customizer': 'true',
          '_unique': Date.now().toString(),
          'Design': '✓ Custom design attached'
        };
        var dtype = mline.getAttribute('data-design-type');
        var dassets = mline.getAttribute('data-design-assets');
        if (dtype) props['_DGS Design Type'] = dtype;
        if (dassets) props['_DGS Design Assets'] = dassets;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'addon_add', addon_type: 'matching_mini' });
        mutate(routes.add, {
          id: parseInt(miniBtn.getAttribute('data-cd-mini'), 10),
          quantity: 1,
          properties: props
        }).catch(function () {
          miniBtn.disabled = false;
          miniBtn.classList.remove('cd-item__miniupsell--busy');
        });
        return;
      }

      var applyBtn = t.closest('[data-cd-discount-apply]');
      if (applyBtn) {
        var input = root.querySelector('[data-cd-discount-input]');
        var code = (input && input.value || '').trim();
        if (!code) return;
        applyBtn.disabled = true;
        mutate(routes.update, { discount: code }).then(function (cart) {
          var codes = (cart && cart.discount_codes) || [];
          var hit = null;
          for (var i = 0; i < codes.length; i++) {
            if (codes[i].code.toLowerCase() === code.toLowerCase()) hit = codes[i];
          }
          if (!hit || !hit.applicable) {
            // drop the dead code so it doesn't ride along to checkout
            return mutate(routes.update, { discount: '' }).then(function () {
              var entry = root.querySelector('.cd__discount-entry');
              var err = root.querySelector('[data-cd-discount-error]');
              var inp = root.querySelector('[data-cd-discount-input]');
              if (entry) entry.open = true;
              if (inp) inp.value = code;
              if (err) {
                err.textContent = '"' + code + '" isn’t a valid code.';
                err.hidden = false;
              }
            });
          }
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'discount_applied', discount_code: hit.code });
        }).catch(function () {});
        return;
      }

      if (t.closest('[data-cd-discount-remove]')) {
        mutate(routes.update, { discount: '' }).catch(function () {});
        return;
      }

      if (t.closest('[data-cd-bubble]')) {
        open(t.closest('[data-cd-bubble]'));
        return;
      }

      if (t.closest('[data-cd-checkout]')) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'checkout_click' });
        // let the form submit normally
      }
    });

    root.addEventListener('input', function (e) {
      var note = e.target.closest('[data-cd-note]');
      if (note) {
        var mirror = root.querySelector('[data-cd-note-mirror]');
        if (mirror) mirror.value = note.value;
      }
    });

    root.addEventListener('change', function (e) {
      var input = e.target.closest('[data-cd-qty-input]');
      if (input) {
        var line = input.closest('.cd-item');
        var qty = Math.max(0, parseInt(input.value, 10) || 0);
        scheduleQty(line, qty);
        return;
      }
      var note = e.target.closest('[data-cd-note]');
      if (note) {
        mutate(routes.update, { note: note.value }).catch(function () {});
      }
    });

    function scheduleQty(lineEl, qty) {
      var key = lineEl.getAttribute('data-key');
      clearTimeout(state.qtyTimers[key]);
      state.qtyTimers[key] = setTimeout(function () {
        changeLine(key, qty, lineEl);
      }, 300);
    }

    root.querySelector('.cd__overlay').addEventListener('click', close);

    // Reorder (buy-again design re-attach) buttons render in the drawer AND
    // on the /cart page — Shopify's Buy again lands customers on the page,
    // so this listener lives at document level.
    document.addEventListener('click', function (e) {
      var reorderBtn = e.target.closest('[data-cd-reorder]');
      if (!reorderBtn || reorderBtn.disabled) return;
      var onCartPage = !root.contains(reorderBtn);
      var rline = reorderBtn.closest('[data-product-id]');
      var rProductId = rline ? rline.getAttribute('data-product-id') : reorderBtn.getAttribute('data-product');
      var rqtyInput = rline ? rline.querySelector('[data-cd-qty-input], input[name="updates[]"]') : null;
      var rqty = rqtyInput ? parseInt(rqtyInput.value, 10) || 1 : 1;
      reorderBtn.disabled = true;
      reorderBtn.classList.add('cd-item__miniupsell--busy');
      var rOrderName = reorderBtn.getAttribute('data-order') || '';
      var rprops = {
        '_DGS Product ID': rProductId,
        '_DGS Variant ID': reorderBtn.getAttribute('data-variant'),
        '_DGS Design URL': reorderBtn.getAttribute('data-url'),
        '_DGS Customizer': 'true',
        '_unique': Date.now().toString(),
        // visible at checkout AND on the order — gives fulfilment the
        // back-reference to the original design's order
        'Design': rOrderName ? '✓ Same design as order ' + rOrderName : '✓ Custom design attached'
      };
      var rt = reorderBtn.getAttribute('data-type');
      var ra = reorderBtn.getAttribute('data-assets');
      var rth = reorderBtn.getAttribute('data-thumb');
      if (rt) rprops['_DGS Design Type'] = rt;
      if (ra) rprops['_DGS Design Assets'] = ra;
      if (rth) rprops['_Disc Design Thumb'] = rth;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'reorder_design_attach' });
      mutate(routes.add, {
        id: parseInt(reorderBtn.getAttribute('data-variant'), 10),
        quantity: rqty,
        properties: rprops
      }).then(function () {
        removeNoDesignOrphan(rProductId);
        return state.queue;
      }).then(function () {
        if (onCartPage) window.location.reload();
      }).catch(function () {
        reorderBtn.disabled = false;
        reorderBtn.classList.remove('cd-item__miniupsell--busy');
      });
    });

    /* ---------- global wiring ---------- */

    // Cart icon: open our drawer instead of the theme's mmenu side cart.
    // Capture phase beats the theme's delegated jQuery handler (bubble phase).
    document.addEventListener(
      'click',
      function (e) {
        var trigger = e.target.closest('a[href="#cart"], a[href$="#cart"].cart-button, .cart-button');
        if (!trigger) return;
        e.preventDefault();
        e.stopPropagation();
        var search = document.getElementById('search');
        if (search) search.style.display = 'none'; // theme's own handler did this; we stopPropagation past it
        open(trigger);
      },
      true
    );

    // Intercept product form submits (including the disc customizer's form —
    // it submits the same product form with its hidden property inputs).
    document.addEventListener('submit', function (e) {
      var form = e.target;
      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;
      e.preventDefault();

      // hard re-entry guard: rapid double/triple clicks were adding 2-3 units
      if (state.addInFlight) return;
      state.addInFlight = true;

      var submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.classList.add('disabled');
        submitBtn.disabled = true;
      }

      var fd = new FormData(form);
      // one deliberately VISIBLE property so checkout shows a reassurance
      // line instead of silence — everything else is underscore-hidden
      var designUrlVal = fd.get('properties[_DGS Design URL]');
      // (a form may bring its own, e.g. the matching-mini link's
      // "✓ Same design as order #N" back-reference for fulfilment)
      if (designUrlVal && String(designUrlVal).indexOf('http') === 0 && !fd.get('properties[Design]')) {
        fd.set('properties[Design]', '✓ Custom design attached');
      }
      fd.append('sections', SECTION);
      fd.append('sections_url', window.location.pathname);

      enqueue(function () {
        return fetch(routes.add + '.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: fd
        })
          .then(function (res) {
            return res.json().then(function (json) {
              if (!res.ok) {
                var err = new Error(json.description || 'Unable to add to cart');
                throw err;
              }
              if (json.sections && json.sections[SECTION]) renderSection(json.sections[SECTION]);
              document.dispatchEvent(new CustomEvent('cart:updated', { detail: json }));
              open(submitBtn);
              maybeRemoveOrphan(json);
            });
          })
          .catch(function (err) {
            var old = form.parentNode.querySelector('.warning');
            if (old) old.remove();
            var warning = document.createElement('p');
            warning.className = 'warning animated bounceIn';
            warning.textContent = err.message;
            form.insertAdjacentElement('afterend', warning);
            // The designer modal covers this page-level warning; let it show
            // the failure itself (listener in customize-disc-design-2.js).
            document.dispatchEvent(new CustomEvent('cart:add-error', {
              detail: { message: err.message }
            }));
          })
          .then(function () {
            state.addInFlight = false;
            if (submitBtn) {
              submitBtn.classList.remove('disabled');
              submitBtn.disabled = false;
            }
          });
      });
    });
  }

  onReady(function () {
    var root = document.getElementById('CartDrawer');
    if (!root) return;
    initDrawer(root);
  });
})();
